import {
    createContext,
    FC,
    PropsWithChildren,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { type Channel as StreamChannel, type Event, StreamChat } from 'stream-chat'

import { inAppEnabledStorage } from '@/helpers/notificationCategoryPreferences'
import { useLocalStorageItem } from '@/hooks/useLocalStorageItem'
import { useSyncOnWindowFocus } from '@/hooks/useSyncOnWindowFocus'
import { setChatUnreadCount } from '@/redux/notifications/notificationsReducer'
import { useTypedDispatch } from '@/redux/store'
import { NotificationCategory } from '@/types'

import { useStreamCredentials } from './StreamCredentialsContext'

const apiKey = process.env.REACT_APP_STREAM_API_KEY ?? ''

const UNREAD_COUNT_EVENT_TYPES = [
    'notification.mark_read',
    'notification.mark_unread',
    'notification.message_new',
    'message.new',
    'notification.added_to_channel',
    'notification.removed_from_channel',
]

interface ChatConnectionContextValue {
    client?: StreamChat
    channels: StreamChannel[]
    unreadCount: number
    userId: string
    // The credentials or the connection itself failed, so `client` won't become available
    hasConnectionError: boolean
    // Registers the conversation the user has open (`undefined` once closed)
    setOpenChannelCid: (cid?: string) => void
}

const ChatConnectionContext = createContext<ChatConnectionContextValue | null>(null)

const createDisconnectedValue = (hasConnectionError: boolean): ChatConnectionContextValue => ({
    channels: [],
    unreadCount: 0,
    userId: '',
    hasConnectionError,
    setOpenChannelCid: () => {},
})

interface ChatClientConnection {
    client: StreamChat | null
    hasConnectionError: boolean
}

/**
 * Like stream-chat-react's `useCreateChatClient`, but exposes a failed `connectUser` instead of
 * leaving `client` at `null` forever. Only reconnects when the user id or token provider changes.
 */
const useChatClient = (userId?: string, tokenProvider?: () => Promise<string>): ChatClientConnection => {
    const [connection, setConnection] = useState<ChatClientConnection>({ client: null, hasConnectionError: false })

    useEffect(() => {
        if (! userId || ! tokenProvider) {
            return undefined
        }

        const client = new StreamChat(apiKey)
        let interrupted = false

        const connectionPromise = client.connectUser({ id: userId }, tokenProvider)
            .then(() => {
                if (! interrupted) {
                    setConnection({ client, hasConnectionError: false })
                }
            })
            .catch(error => {
                console.error('[useChatClient]', error)

                if (! interrupted) {
                    setConnection({ client: null, hasConnectionError: true })
                }
            })

        return () => {
            interrupted = true
            setConnection({ client: null, hasConnectionError: false })
            connectionPromise.then(() => client.disconnectUser()).catch(error => console.error(error))
        }
    }, [userId, tokenProvider])

    return connection
}

// Only connects once the current user's Stream credentials are known, but always renders the same tree:
// swapping it at that moment would remount (and reset) the current page.
export const ChatConnectionProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
    const dispatch = useTypedDispatch()
    const credentials = useStreamCredentials()
    const userId = credentials.status === 'ready' ? credentials.userId : undefined
    const tokenProvider = credentials.status === 'ready' ? credentials.tokenProvider : undefined
    const { client, hasConnectionError } = useChatClient(userId, tokenProvider)

    const [liveUnreadCount, setLiveUnreadCount] = useState<number>()
    const [channels, setChannels] = useState<StreamChannel[]>([])
    const openChannelCidRef = useRef<string>(undefined)

    const refreshChannels = useCallback((): void => {
        client?.queryChannels(
            { members: { $in: [client.userID ?? ''] }, type: { $in: ['team', 'messaging'] } },
            { last_message_at: -1 },
            { state: true, watch: true },
        ).then(setChannels).catch(error => console.error(error))
    }, [client])

    const refreshUnreadCount = useCallback((): void => {
        client?.getUnreadCount()
            .then(response => setLiveUnreadCount(response.total_unread_count))
            .catch(error => console.error(error))
    }, [client])

    // Messages received while the connection was down (e.g. a backgrounded app, which only got a push) bring no events
    const resync = useCallback((): void => {
        refreshUnreadCount()
        refreshChannels()
    }, [refreshUnreadCount, refreshChannels])

    useSyncOnWindowFocus(resync)

    useEffect(() => {
        if (! client) {
            return undefined
        }

        refreshChannels()

        const handleUnreadCountEvent = (event: Event): void => {
            // A new message in the open conversation counts as unread until Stream marks it read right after,
            // which would briefly show the indicator. Skipped, as the next event (or closing it) brings the count
            const isInOpenChannel = event.type === 'message.new'
                && event.cid === openChannelCidRef.current
                && ! document.hidden

            if (event.total_unread_count !== undefined && ! isInOpenChannel) {
                setLiveUnreadCount(event.total_unread_count)
            }

            refreshChannels()
        }

        const subscriptions = [
            ...UNREAD_COUNT_EVENT_TYPES.map(eventType => client.on(eventType, handleUnreadCountEvent)),
            client.on('connection.recovered', resync),
        ]

        return () => {
            subscriptions.forEach(subscription => subscription.unsubscribe())
        }
    }, [client, refreshChannels, resync])

    const setOpenChannelCid = useCallback((cid?: string): void => {
        const previousCid = openChannelCidRef.current
        openChannelCidRef.current = cid

        // Messages that stayed unread in the closed conversation (e.g. while scrolled up) were skipped above
        if (previousCid && ! cid) {
            refreshUnreadCount()
        }
    }, [refreshUnreadCount])

    const clientUser = client?.user
    const storedUnreadCount = clientUser && 'total_unread_count' in clientUser ? clientUser.total_unread_count : undefined
    const rawUnreadCount = liveUnreadCount ?? storedUnreadCount ?? 0

    const [chatMessagesInAppEnabled] = useLocalStorageItem(inAppEnabledStorage(NotificationCategory.chatMessages, userId ?? ''))

    const notificationUnreadCount = chatMessagesInAppEnabled ? rawUnreadCount : 0

    useEffect(() => {
        dispatch(setChatUnreadCount(notificationUnreadCount))
    }, [dispatch, notificationUnreadCount])

    const value = useMemo((): ChatConnectionContextValue => (! userId ? (
        createDisconnectedValue(credentials.status === 'unavailable')
    ) : {
        client: client ?? undefined,
        channels,
        unreadCount: notificationUnreadCount,
        userId,
        hasConnectionError,
        setOpenChannelCid,
    }), [credentials.status, client, channels, notificationUnreadCount, userId, hasConnectionError, setOpenChannelCid])

    return (
        <ChatConnectionContext.Provider value={value}>
            {children}
        </ChatConnectionContext.Provider>
    )
}

export const useChatConnection = (): ChatConnectionContextValue => {
    const context = useContext(ChatConnectionContext)

    if (! context) {
        throw new Error('useChatConnection must be used within a ChatConnectionProvider')
    }

    return context
}
